import { createContext } from 'react';
import { decorate } from 'mobx';

import Leanplum from 'leanplum-sdk';
import { AuthAgent } from 'agents';

// leanplum doesn't get loaded in 5% of times for different reasons out of our control
// always check it before calling it
export const leanplumNotFound = () => !('Leanplum' in window);

function initLeanPlum() {
    (window as any).Leanplum = Leanplum;

    if (!(window as any).yconfig.Leanplum) return;

    Leanplum[
        (window as any).yconfig.Leanplum.production
            ? 'setAppIdForProductionMode'
            : 'setAppIdForDevelopmentMode'
    ](
        (window as any).yconfig.Leanplum.appId,
        (window as any).yconfig.Leanplum.key,
    );

    // Set app version (needed for app-version specific A/B tests to be always active on the web)
    Leanplum.setAppVersion('5.0');

    Leanplum.setVariables({
        RevenueWeb: {
            CheckoutForm: 'form',
            DashboardSubscribeBanner: 'plain',
        },

        Revenue: {
            Paypal: false,
            LicensedContent: false,
            'Gifting Price Points':
                'quarterly_licensed, semiannual_licensed, yearly_licensed',
            'PremiumPlus Instruments': 'guitar, bass, ukulele, voice, piano',
        },
        Voice: {
            'Allow Access': false,
        },
    });
}

initLeanPlum();

class LeanplumStore {
    start() {
        if (leanplumNotFound()) return Promise.resolve();

        return new Promise((resolve) => {
            (window as any).Leanplum.start(resolve);
        });
    }

    getVariantsString() {
        return leanplumNotFound()
            ? ''
            : (window as any).Leanplum.getVariants()
                  .map((variant: any) => variant.id)
                  .join(',');
    }

    setUserVariants(data: any) {
        const user_id = data.user_id;
        const attributes = data.leanplum_attributes;
        Leanplum.setUserId(user_id);
        Leanplum.setUserAttributes(attributes);
        Leanplum.forceContentUpdate((callback) => {
            // Usually this is false if the requests to leanplum are blocked by adblocker
            if (!callback) {
                AuthAgent.leanplum_initialize();
                Leanplum.forceContentUpdate();
            }
        });
    }

    loginLeanplum(user_id: string) {
        Leanplum.setUserId(user_id);
        Leanplum.forceContentUpdate();
    }

    variables(group = 'Revenue') {
        return leanplumNotFound()
            ? {}
            : (window as any).Leanplum.getVariables()[group];
    }

    monthlyInstallments() {
        return this.variables().YearlyMonthly;
    }

    experiment(key: string, group = 'RevenueWeb') {
        return this.variables(group)[key];
    }

    alternativeOfferIos() {
        return this.variables()['Alternative iOS plan offer'];
    }
}

decorate(LeanplumStore, {});

export const leanplumStore = new LeanplumStore();
export default createContext(leanplumStore);
